import { eAnimAssets, eAtlasFrame, eManulSortLayerDrawOrder, eModelAsset, eTextures, Game, rnd2 } from "../../Game";
import { AtlasUtils } from "../../Utils/Atlas/AtlasUtils";
import { Gameplay, TIMER } from "../Gameplay";
import { Ball, eKickOffResult, KickOffResEstimate } from "../Player/Ball";
import { ChantingSfxManager } from "./ChantingSfx";
import { Grandstand } from "./Grandstand";
import { Guards } from "./Guards";
import { NetImpactSimulation } from "./NetHitSimulation";



export const enum ePhysicsObjects {
    /**
     * Zem
     */
    ground = "ground",
    /**
     * Zadni strana brany
     */
    goalBackSide = "goalBackSide",
    /**
     * Leva strana brany
     */
    goalLeftSide = "goalLeftSide",
    /**
     * Prava strana brany
     */
    goalRightSide = "goalRightSide",
    /**
     * Ram branky
     */
    goalBar = "goalBar",
    /**
     * Vnitrek branky
     */
    goalTrigger = "goalTrigger",
    /**
     * Terc
     */
    target = "target",
    /**
     * Obrance
     */
    defender = "defender",
    /**
     * Mic
     */
    ball = "ball"
}



export class Stadium {
    static readonly CENTER_X = 0;
    static readonly CENTER_Z = 0;

    static readonly WIDTH = 40;
    static readonly LENGTH = 90;
    static readonly END_Z = Stadium.CENTER_Z - Stadium.LENGTH / 2;

    static readonly GROUND_WIDTH = Stadium.WIDTH + 8;
    static readonly GROUND_LENGTH = Stadium.LENGTH + 16;
    static readonly GROUND_HEIGHT = 1;

    static readonly GOAL_WIDTH = 7.32;
    static readonly GOAL_HALF_WIDTH = Stadium.GOAL_WIDTH / 2;
    static readonly GOAL_HEIGHT = 2.44;
    static readonly GOAL_Z = Stadium.CENTER_Z - Stadium.LENGTH / 2;

    static readonly PENALTY_MARK_Z = Stadium.END_Z + 11;
    static readonly PENALTY_ARC_RADIUS = 9.15;

    static readonly GOALIE_Z = Stadium.END_Z + 0.4;
    static readonly GOALIE_DEFEND_Z = Stadium.GOALIE_Z + 0.2;

    private static readonly AD_BANNER_SCROLL_SPEED = 0.1;

    private _adBannerMat: pc.StandardMaterial[];

    get grandstand(): Grandstand { return this._grandstand; }
    private _grandstand: Grandstand;

    private _netSimulation: NetImpactSimulation;
    private _craneCam: pc.Entity;
    private _guards: Guards;
    private _chantingSfx: ChantingSfxManager;

    private _vcBatchGroupId: number;

    private _matOverlay: pc.StandardMaterial;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        let root = Game.app.root;

        this._vcBatchGroupId = Game.app.batcher.addGroup("stadiumStatic", false, 400).id;

        this.createAdBanner(root);
        this.createGround(root);
        this.createGoalCameras(root);
        this.createGoal(root);
        this.createCraneCam(root);
        this.createGrandstandOverlay();

        this._guards = new Guards();
        this._grandstand = new Grandstand();
        this._chantingSfx = new ChantingSfxManager();

        Gameplay.instance.events.on(Ball.EVENT_KICKED_OFF2, this.handleBallFlightEnd, this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._grandstand.reset();
        this._chantingSfx.reset();
        this._netSimulation.reset();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    prepareNewRound(): void {
        this._craneCam.animation.currentTime = 0;
        this._craneCam.animation.speed = 0;

        this._grandstand.prepareNewRound();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        this._adBannerMat.forEach((mat: pc.StandardMaterial): void => {
            mat.emissiveMapOffset.x -= Stadium.AD_BANNER_SCROLL_SPEED * TIMER.deltaS;
            mat.update();
        });

        let i = TIMER.deltaS * 0.01;

        this._matOverlay.opacityMapOffset.x = (this._matOverlay.diffuseMapOffset.x += i);
        this._matOverlay.update();

        this._grandstand.update();
        this._chantingSfx.update();

        this._netSimulation.update();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleBallFlightEnd(res: KickOffResEstimate): void {
        if (res.result == eKickOffResult.target || res.result == eKickOffResult.goal) {
            this._craneCam.animation.play(eAnimAssets.craneCam, 0);
            this._craneCam.animation.speed = 1;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private createGrandstandOverlay(): void {
        let mat = this._matOverlay = new pc.StandardMaterial();

        mat.diffuseMap = Game.app.assets.find(eTextures.grandstandOverlay, pc.eAssetType.texture).resource;
        mat.opacityMap = mat.diffuseMap;
        mat.blendType = pc.BLEND_NORMAL;
        mat.depthWrite = false;
        this._matOverlay.opacityMapOffset.x = (this._matOverlay.diffuseMapOffset.x = rnd2.random());
        mat.update();

        let grandstandOverlay = new pc.Entity();
        grandstandOverlay.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.plane,
            castShadows: false,
            receiveShadows: false,
            isStatic: true,
            material: mat,
            layers: [Game.manualSortLayer.id]
        });
        //grandstandOverlay.model.layers = [Game.app.scene.layers.getLayerByName("World").id];
        grandstandOverlay.model.meshInstances[0].drawOrder = eManulSortLayerDrawOrder.grandstandOverlay;
        grandstandOverlay.setPosition(0, 8, -67);
        grandstandOverlay.setEulerAngles(68, 0, 0);
        grandstandOverlay.setLocalScale(80, 1, 24);
        Game.app.root.addChild(grandstandOverlay);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private createCraneCam(root: pc.GraphNode): void {
        this._craneCam = new pc.Entity();
        this._craneCam.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: Game.app.assets.find(eModelAsset.craneCam, pc.eAssetType.model),
            castShadows: false,
            receiveShadows: false,
            isStatic: true,
        });
        this._craneCam.addComponent(pc.eCompomentType.animation, {
            assets: [
                Game.app.assets.find(eAnimAssets.craneCam, pc.eAssetType.animation),
            ],
            speed: 1,
            activate: false,
            loop: false
        });
        this._craneCam.setPosition(-2, 0, Stadium.END_Z);
        root.addChild(this._craneCam);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private createGround(root: pc.GraphNode): void {
        let ground = new pc.Entity(ePhysicsObjects.ground);

        root.addChild(ground);

        // Grafika
        ground.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: Game.app.assets.find(eModelAsset.ground, pc.eAssetType.model),
            castShadows: false,
            receiveShadows: true,
            isStatic: true,
            batchGroupId: this._vcBatchGroupId
        });

        ground.setLocalPosition(0, -Stadium.GROUND_HEIGHT / 2, 0);

        // Fyzika zeme
        ground.addComponent(pc.eCompomentType.rigidbody, {
            type: pc.BODYTYPE_STATIC,
            mass: 1,
            restitution: 0.6,
            friction: 0.75,
        });

        ground.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.box,
            halfExtents: new pc.Vec3(Stadium.GROUND_WIDTH / 2, Stadium.GROUND_HEIGHT / 2, Stadium.GROUND_LENGTH / 2),
        });
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private createAdBanner(root: pc.GraphNode): void {
        let bannerEnt: pc.Entity[] = [];
        let bannerSize: pc.Vec2[] = [
            new pc.Vec2(40, 1.4),
            //new pc.Vec2(70, 1.4)
        ];
        let bannerPos: pc.Vec3[] = [
            new pc.Vec3(0, 0.7, -50),
            //new pc.Vec3(0, 16.5, -71)
        ]

        /*let atlas = Game.atlas;
        let bannerFrameRect = (atlas.frames[eAtlasFrame.adBanner] as pc.TextureAtlasFrame).rect;
        let bannerFrameRectUV = TextureAtlasUtils.getTexFrameRegion(atlas.texture, bannerFrameRect);*/
        let texture = Game.app.assets.find(eTextures.banner, pc.eAssetType.texture).resource as pc.Texture;

        this._adBannerMat = [];

        bannerSize.forEach((size: pc.Vec2, bannerId: number): void => {
            // Material
            let mat = new pc.StandardMaterial();

            mat.diffuse.set(0, 0, 0);
            mat.emissiveMap = texture;
            mat.emissiveMapTiling.set(size.x / ((size.y / texture.height) * texture.width), 1);
            //mat.emissiveMapTiling.set(size.x / ((size.y / bannerFrameRect.w) * bannerFrameRect.z), bannerFrameRectUV.w);
            //mat.emissiveMapOffset.set(bannerFrameRectUV.x, bannerFrameRectUV.y);
            mat.update();

            this._adBannerMat.push(mat);

            // Entita
            let ent = new pc.Entity();

            bannerEnt.push(ent);
            root.addChild(ent);

            ent.addComponent(pc.eCompomentType.model, {
                type: pc.eModelType.plane,
                castShadows: false,
                receiveShadows: false,
                isStatic: true,
                material: mat
            });
            ent.setEulerAngles(90, 0, 0);
            ent.setLocalPosition(bannerPos[bannerId]);
            ent.setLocalScale(size.x, 1, size.y);
        });

        // Fyzika
        bannerEnt[0].addComponent(pc.eCompomentType.rigidbody, {
            type: pc.BODYTYPE_STATIC,
            mass: 1,
            restitution: 0.25,
            friction: 0.5,
        });

        bannerEnt[0].addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.box,
            halfExtents: new pc.Vec3(bannerSize[0].x / 2, 0.5, bannerSize[0].y / 2),
        });
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private createGoalCameras(root: pc.GraphNode): void {
        this.createGoalCam(root, new pc.Vec3(5.5, 0, -47), new pc.Vec3(0, -45, 0));
        this.createGoalCam(root, new pc.Vec3(-5.5, 0, -47), new pc.Vec3(0, 45, 0));
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private createGoalCam(root: pc.GraphNode, pos: pc.Vec3, rot: pc.Vec3): pc.Entity {
        let e = new pc.Entity("goalCam");

        e.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: Game.app.assets.find(eModelAsset.goalCam, pc.eAssetType.model),
            castShadows: true,
            receiveShadows: false,
            isStatic: true,
            batchGroupId: this._vcBatchGroupId
        });

        e.setPosition(pos);
        e.setLocalEulerAngles(rot);

        root.addChild(e);

        return e;
    }

    //-------------------------------------------------------------------------
    /**
     * Vytvoreni branky
     */
    //-------------------------------------------------------------------------
    private createGoal(root: pc.GraphNode): void {
        // Branka
        let goal = new pc.Entity();

        goal.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: Game.app.assets.find(eModelAsset.goalFrame, pc.eAssetType.model),
            castShadows: true,
            receiveShadows: false,
            isStatic: true,
            batchGroupId: this._vcBatchGroupId
        });
        goal.setLocalPosition(0, 0, Stadium.END_Z)
        root.addChild(goal);

        let net = new pc.Entity();

        net.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: Game.app.assets.find(eModelAsset.goalNet, pc.eAssetType.model),
            castShadows: false,
            receiveShadows: false,
            isStatic: true
        });
        goal.addChild(net);

        // Simulace zasahu do site
        this._netSimulation = new NetImpactSimulation(net.model.meshInstances[0].morphInstance);

        // Opravit cull material site
        let mat = net.model.meshInstances[0].material as pc.StandardMaterial;

        mat.cull = pc.CULLFACE_NONE;
        mat.emissive.set(1, 1, 1);
        mat.useLighting = false;
        mat.update();

        // --------------------------------------------------------------------
        // Fyzika vnitrku branky (site)
        // --------------------------------------------------------------------
        goal.addComponent(pc.eCompomentType.rigidbody, {
            type: pc.BODYTYPE_STATIC,
            mass: 1,
            restitution: 0,
            friction: 0.9,
        });
        goal.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.compound,
        });
        goal.rigidbody.on("collisionstart", (result: pc.ContactResult): void => {
            if (result.other.name == ePhysicsObjects.ball) {
                let contact = result.contacts[0];

                this._netSimulation.startBackWallImpact(contact.point, contact.impulse);
            }
        });

        // Zadni stena site
        let colPart = new pc.Entity(ePhysicsObjects.goalBackSide);

        colPart.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.box,
            halfExtents: new pc.Vec3(3.75, 1.5, 0.25)
        });
        colPart.setLocalPosition(0, 1, -3);
        colPart.setEulerAngles(12, 0, 0);
        goal.addChild(colPart);

        // Bocni steny
        let goalSides = new pc.Entity();

        goalSides.addComponent(pc.eCompomentType.rigidbody, {
            type: pc.BODYTYPE_STATIC,
            mass: 1,
            restitution: 0,
            friction: 0.9,
        });
        goalSides.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.compound,
        });
        goalSides.rigidbody.on("collisionstart", (result: pc.ContactResult): void => {
            if (result.other.name == ePhysicsObjects.ball) {
                let contact = result.contacts[0];
                this._netSimulation.startSideWallImpact(contact.point, contact.impulse);
            }
        });
        goal.addChild(goalSides);

        // Prava stena site
        colPart = new pc.Entity();

        colPart.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.box,
            halfExtents: new pc.Vec3(0.15, 1.3, 1.55)
        });
        colPart.setLocalPosition(3.75, 1.259, -1.65);
        goalSides.addChild(colPart);

        // Leva stena site
        colPart = new pc.Entity();

        colPart.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.box,
            halfExtents: new pc.Vec3(0.15, 1.3, 1.55)
        });
        colPart.setLocalPosition(-3.75, 1.259, -1.65);
        goalSides.addChild(colPart);

        // Horni stena site
        colPart = new pc.Entity();

        colPart.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.box,
            halfExtents: new pc.Vec3(3.75, 0.1, 1.45)
        });
        colPart.setLocalPosition(0, 2.46, -1.55);
        goal.addChild(colPart);

        // --------------------------------------------------------------------
        // Fyzika ramu branky
        // --------------------------------------------------------------------
        let goalFrame = new pc.Entity(ePhysicsObjects.goalBar);

        goalFrame.addComponent(pc.eCompomentType.rigidbody, {
            type: pc.BODYTYPE_STATIC,
            mass: 1,
            restitution: 0.6,
            friction: 0.5
        });

        goalFrame.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.compound,
        });

        goalFrame.setLocalPosition(0, 0, Stadium.END_Z);
        root.addChild(goalFrame);

        // Leva cast ramu
        colPart = new pc.Entity();
        colPart.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.box,
            halfExtents: new pc.Vec3(0.15, 1.3, 0.15)
        });
        colPart.setLocalPosition(-3.7, 1.259, 0.05);
        goalFrame.addChild(colPart);

        // Prava cast ramu
        colPart = new pc.Entity();
        colPart.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.box,
            halfExtents: new pc.Vec3(0.15, 1.3, 0.15)
        });
        colPart.setLocalPosition(3.7, 1.259, 0.05);
        goalFrame.addChild(colPart);

        // Horni cast ramu
        colPart = new pc.Entity();
        colPart.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.box,
            halfExtents: new pc.Vec3(3.75, 0.1, 0.15)
        });
        colPart.setLocalPosition(0, 2.46, 0.05);
        goalFrame.addChild(colPart);

        // Goal trigger
        let trigger = new pc.Entity();

        trigger.name = ePhysicsObjects.goalTrigger;
        trigger.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.box,
            halfExtents: new pc.Vec3(3.5, 1.15, 0.5)
        });
        trigger.setLocalPosition(0, 1.1, Stadium.END_Z - 1);
        trigger.collision.on("triggerenter", (other: pc.Entity) => {
            other.collision.fire("triggerenter", trigger);
        }, this);
        root.addChild(trigger);
    }
}